import {Component, OnInit, ViewChild} from '@angular/core';
import {BaseView} from '../../../../../../core/view/base.view';
import {
    FormControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {
    CustomDateInputComponent
} from '../../../../../../../components/common/date/custom-date/custom-date-input/custom-date-input.component';
import {MedicalRecordProblemModel} from '../../model/medicalRecordProblem.model';
import {ObjectUtils} from '../../../../../../../shared/utils/object.utils';
import {
    MedicalRecordProblemCreateUpdatePayload
} from '../../../impl/service/payload/medicalRecordProblemCreateUpdate.payload';
import {CustomDateModel} from '../../../../../../common/custom-date/entity/custom-date.model';
import {Order, Sort} from '../../../../../../../shared/utils/url.builder';
import {PartnerType} from '../../../../../../partner/common/api/model/partnerSummary.model';
import {LovValueSummary} from '../../../../../../common/lov/lov-value/api/model/lovValueSummary.model';
import {MedicalProblemSaveOneService} from '../../../impl/service/medicalProblemSaveOne.service';
import {MedicalProblemFindOneByIdService} from '../../../impl/service/medicalProblemFindOneById.service';
import {Moment} from 'moment';
import {ConsultationSummaryModel} from '../../../../../consultation/consultation/api/model/consultationSummary.model';
import {IDialogComponent} from '../../../../../../common/dialog/iDialog.component';
import {DialogWithInput} from '../../../../../../common/dialog/dialogWithInput';
import {
    CreateUpdateDefaultComponent
} from '../../../../../../core/component/create-update-default/create-update-default.component';
import {
    AudioTranscriptionConfig
} from "../../../../../../common/audio/transcription/api/component/audio-transcription-v2/audio-transcription-v2.component";
import {
    ConsultationTranscriptionHandler
} from "../../../../../consultation/consultation/api/service/transcription/consultationTranscriptionHandler";

export interface MedicalProblemSaveDialogInput {
    id?: number;
    partnerId?: number;
    consultationSummary?: ConsultationSummaryModel;
    startTranscription?: boolean;
}
export interface MedicalProblemSaveDialogResult {
    entity: MedicalRecordProblemModel;
    created: boolean;
}

@Component({
    selector: 'app-medical-record-problem-create-update',
    templateUrl: './medical-record-problem-create-update.component.html',
    providers: [
        MedicalProblemSaveOneService,
        MedicalProblemFindOneByIdService,
        ConsultationTranscriptionHandler
    ]
})
export class MedicalRecordProblemCreateUpdateComponent extends BaseView
    implements OnInit, IDialogComponent<MedicalProblemSaveDialogInput, MedicalProblemSaveDialogResult> {
    dialog: DialogWithInput<MedicalProblemSaveDialogInput, MedicalProblemSaveDialogResult>;

    PRACTITIONER = PartnerType.PARTNER_TYPE_PRACTITIONER;
    formGroup: UntypedFormGroup;
    involvedPartnerFormArray: UntypedFormArray;

    @ViewChild('from') fromComponent: CustomDateInputComponent;
    @ViewChild('to') toComponent: CustomDateInputComponent;
    @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent: CreateUpdateDefaultComponent<any, any, any>;

    toInitialDate: Moment;
    toInitialTypeValue: string;

    lovValueSort = new Sort('parent.label,label', Order.ASCENDING);
    descriptionAudioTranscription: AudioTranscriptionConfig;
    treatmentAudioTranscription: AudioTranscriptionConfig;

    constructor(public service: MedicalProblemSaveOneService,
                public findOneService: MedicalProblemFindOneByIdService,
                private formBuilder: UntypedFormBuilder,
                public transcriptionHandler: ConsultationTranscriptionHandler) {
        super();

        this.involvedPartnerFormArray = new UntypedFormArray([]);
        this.formGroup = this.formBuilder.group({
                id: new UntypedFormControl(''),
                partnerId: new UntypedFormControl('', Validators.required),
                consultationId: new FormControl(undefined),
                categoryValue: new UntypedFormControl('MEDICAL_RECORD_PROBLEM_TYPE_OTHER_PARENT', Validators.required),
                typeValue: new UntypedFormControl('MEDICAL_RECORD_PROBLEM_TYPE_OTHER', Validators.required),
                description: new UntypedFormControl(''),
                locations: new UntypedFormControl([]),
                whens: new UntypedFormControl([]),
                stillHere: new FormControl(true),
                treatment: new UntypedFormControl(''),
                comment: new UntypedFormControl(''),
                from: new UntypedFormGroup({}, Validators.required),
                involvedPartners: this.involvedPartnerFormArray
            }
        );
    }

    ngOnInit() {
        if (! this.dialog.input.id) {
            if (this.dialog.input.partnerId) {
                this.formGroup.patchValue({
                    partnerId: this.dialog.input.partnerId,
                });
            } else if (this.dialog.input.consultationSummary) {
                this.formGroup.patchValue({
                    partnerId: this.dialog.input.consultationSummary.partner.id,
                    consultationId: this.dialog.input.consultationSummary.id,
                });
            }
            this.transcriptionHandler.setup({
                consultationId: this.dialog.input.consultationSummary?.id,
                partnerId: this.dialog.input.consultationSummary?.partner?.id
            });

            this.descriptionAudioTranscription = {
                startAutomatically: this.dialog.input.startTranscription,
                context: 'partner.id=' + this.formGroup.get('partnerId').value,
                audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_ANTECEDENT',
                enableConversationOption: false,
                handler: this.transcriptionHandler,
                showPreview: true,
            };

            this.treatmentAudioTranscription = {
                startAutomatically: false,
                context: 'partner.id=' + this.formGroup.get('partnerId').value,
                audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_PROBLEM_TREATMENT',
                enableConversationOption: false,
                handler: this.transcriptionHandler,
                showPreview: true,
            };
        }

    }

    synchronizeStillHereControl() {
        if (this.formGroup.get('stillHere').value === true) {
            this.formGroup.removeControl('to');
        } else {
            if (!this.formGroup.get('to')) {
                this.formGroup.addControl('to', new UntypedFormGroup({}));
            }
        }
    }

    entityLoaded(entity: MedicalRecordProblemModel) {
        this.formGroup.patchValue({
            id: entity.id,
            partnerId: entity.partner.id,
            consultationId: entity.consultation?.id,
            categoryValue: entity.type.parentValue,
            typeValue: entity.type.value,
            description: entity.description,
            locations: entity.locations?.map(value => value.lovValue ? value.lovValue.value : value.customValue),
            whens: entity.whens?.map(value => value.lovValue ? value.lovValue.value : value.customValue),
            treatment: entity.treatment,
            comment: entity.comment,
            from: entity.from ? this.mapCustomDate(entity.from) : undefined,
            stillHere: !entity.to,
        });
        this.synchronizeStillHereControl();


        this.fromComponent.entityLoaded(entity.from);

        if (entity.to) {
            this.toInitialDate = entity.to.date;
            this.toInitialTypeValue = entity.to.type.value;
        }

        entity.involvedPartners.forEach(value => {
            const itemFormGroup = this.newItemFormGroup();
            itemFormGroup.patchValue({
                partnerId: value.id
            });
        });

        this.transcriptionHandler.setup({
            consultationId: entity.consultation?.id,
            partnerId: entity.partner?.id
        });

        let context: string;
        if (!entity.consultation) {
            context = 'partnerId=' + entity.partner?.id;
        } else {
            context = 'consultationId=' + entity.consultation.id;
        }

        this.descriptionAudioTranscription = {
            startAutomatically: this.dialog.input.startTranscription,
            context,
            audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_ANTECEDENT',
            enableConversationOption: false,
            handler: this.transcriptionHandler,
            showPreview: true,
        };

        this.treatmentAudioTranscription = {
            startAutomatically: false,
            context,
            audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_PROBLEM_TREATMENT',
            enableConversationOption: false,
            handler: this.transcriptionHandler,
            showPreview: true,
        };

    }

    buildPayload = (): MedicalRecordProblemCreateUpdatePayload => {
        const payload = ObjectUtils.assign(new MedicalRecordProblemCreateUpdatePayload(), this.formGroup.value, ['involvedPartners']);
        payload.from = this.fromComponent.buildPayload();
        payload.to = this.toComponent?.buildPayload();
        payload.involvedPartnerIds = this.involvedPartnerFormArray.controls
            .map((value: UntypedFormGroup) => value.controls.partnerId.value);
        return payload;
    }

    entitySaved(entity: MedicalRecordProblemModel) {
        this.dialog.close({
            created: !!this.formGroup.get('id').value,
            entity
        });
    }

    newItemFormGroup = (): UntypedFormGroup => {
        return this.formBuilder.group({
            partnerId: new UntypedFormControl(undefined, Validators.required),
        });
    }

    mapCustomDate(customDateModel: CustomDateModel) {
        return this.formBuilder.group({
            typeValue: customDateModel.type.value,
            date: customDateModel.date,
        });
    }

    filterMedicalRecordProblemType = (criteria: string, lovValue: LovValueSummary) => {
        return lovValue.label.toUpperCase().includes(criteria.toUpperCase())
            || lovValue.parentLabel?.toUpperCase().includes(criteria.toUpperCase());
    }

    executeSave() {
        this.createUpdateDefaultComponent.save();
    }
}
